<template>
	<header
		class="flex items-center justify-between h-16 px-6 bg-surface dark:bg-dark-700 border-b border-primary-100 dark:border-dark-600 shadow-nav transition-colors duration-300 relative">
		<!-- ── LEFT: Hamburger + Search ── -->
		<div class="flex items-center gap-4 flex-1">
			<!-- Hamburger — mobile only -->
			<button
				@click="$emit('toggleSidebar')"
				class="md:hidden flex items-center justify-center w-9 h-9 rounded-xl bg-primary-50 dark:bg-dark-600 text-dark-700 dark:text-white/70 hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors duration-200">
				<svg
					class="w-4 h-4"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2.5"
						d="M4 6h16M4 12h16M4 18h16" />
				</svg>
			</button>

			<!-- Search — desktop only -->
			<div
				class="hidden md:flex items-center gap-2.5 w-72 px-3.5 py-2 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 focus-within:border-primary-400 dark:focus-within:border-primary-400 transition-colors duration-200">
				<svg
					class="w-4 h-4 text-muted dark:text-muted-light flex-shrink-0"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
				</svg>
				<input
					type="text"
					placeholder="Search task"
					class="bg-transparent text-[13px] text-dark-700 dark:text-white/80 placeholder-muted dark:placeholder-muted-light outline-none w-full font-sans" />
				<span
					class="hidden lg:inline-flex items-center text-[11px] font-medium text-muted dark:text-muted-light bg-primary-100 dark:bg-dark-500 border border-primary-200 dark:border-dark-400 rounded-md px-1.5 py-0.5 flex-shrink-0">
					⌘ F
				</span>
			</div>
		</div>

		<!-- ── RIGHT: Bell + Profile ── -->
		<div class="flex items-center gap-3">
			<!-- Notification bell -->
			<button
				class="relative flex items-center justify-center w-9 h-9 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 text-muted dark:text-muted-light hover:bg-primary-100 dark:hover:bg-dark-500 hover:text-primary-400 transition-all duration-200">
				<svg
					class="w-4 h-4"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0
                   .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
				</svg>
				<span
					class="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-primary-400" />
			</button>

			<!-- ── Desktop profile dropdown ── -->
			<div class="relative hidden md:block">
				<button
					@click="toggleDropdown"
					class="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 hover:bg-primary-100 dark:hover:bg-dark-500 transition-all duration-200">
					<!-- Avatar -->
					<div
						class="w-8 h-8 rounded-lg bg-primary-400 flex items-center justify-center flex-shrink-0 shadow-teal">
						<span class="font-display text-[12px] font-extrabold text-white">
							{{ initials }}
						</span>
					</div>
					<!-- Name + email -->
					<div class="text-left hidden lg:block">
						<p
							class="text-[13px] font-semibold text-dark-700 dark:text-white leading-none mb-0.5 font-display">
							{{ userInfo.organization_name }}
						</p>
						<p
							class="text-[11px] text-muted dark:text-muted-light leading-none">
							{{ userInfo.email }}
						</p>
					</div>
					<svg
						class="w-3.5 h-3.5 text-muted dark:text-muted-light ml-1 flex-shrink-0 transition-transform duration-200"
						:class="isDropdownOpen ? 'rotate-180' : ''"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2.5"
							d="M19 9l-7 7-7-7" />
					</svg>
				</button>

				<!-- Dropdown -->
				<transition name="dropdown">
					<div
						v-if="isDropdownOpen"
						class="absolute right-0 mt-2 w-52 rounded-2xl z-50 overflow-hidden bg-surface dark:bg-dark-700 border border-primary-100 dark:border-dark-600 shadow-dropdown">
						<!-- User info header -->
						<div
							class="px-4 py-3 border-b border-primary-50 dark:border-dark-600">
							<p
								class="text-[13px] font-semibold text-dark-700 dark:text-white font-display">
								{{ userInfo.organization_name }}
							</p>
							<p class="text-[11px] text-muted dark:text-muted-light mt-0.5">
								{{ userInfo.email }}
							</p>
						</div>

						<div class="py-1.5">
							<button
								v-if="
									(forClient || forAdmin) && userInfo.can_manage_org == true
								"
								@click="accountSettings"
								class="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-[13px] text-dark-700 dark:text-white/80 hover:bg-primary-50 dark:hover:bg-dark-600 transition-colors duration-150">
								<svg
									class="w-4 h-4 text-muted dark:text-muted-light"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94
                           3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724
                           1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572
                           1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31
                           -.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724
                           1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
								</svg>
								Edit organization
							</button>

							<button
								v-else
								class="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-[13px] text-dark-700 dark:text-white/80 hover:bg-primary-50 dark:hover:bg-dark-600 transition-colors duration-150">
								<svg
									class="w-4 h-4 text-muted dark:text-muted-light"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
								</svg>
								{{ userInfo.name }}
							</button>

							<div
								class="mx-3 my-1 border-t border-primary-50 dark:border-dark-600" />

							<button
								type="button"
								@click="logout"
								class="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-[13px] text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors duration-150">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
								</svg>
								Logout
							</button>
						</div>
					</div>
				</transition>
			</div>

			<!-- ── Mobile profile dropdown ── -->
			<div class="relative md:hidden">
				<button
					@click="toggleDropdown"
					class="w-9 h-9 rounded-xl bg-primary-400 flex items-center justify-center shadow-teal hover:bg-primary-600 transition-colors duration-200">
					<span class="font-display text-[12px] font-extrabold text-white">{{
						initials
					}}</span>
				</button>

				<transition name="dropdown">
					<div
						v-if="isDropdownOpen"
						class="absolute right-0 mt-2 w-48 rounded-2xl z-50 overflow-hidden bg-surface dark:bg-dark-700 border border-primary-100 dark:border-dark-600 shadow-dropdown">
						<div class="py-1.5">
							<button
								@click="accountSettings"
								class="w-full text-left px-4 py-2.5 text-[13px] text-dark-700 dark:text-white/80 hover:bg-primary-50 dark:hover:bg-dark-600 transition-colors duration-150">
								Edit organization
							</button>
							<button
								@click="logout"
								class="w-full text-left px-4 py-2.5 text-[13px] text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors duration-150">
								Logout
							</button>
						</div>
					</div>
				</transition>
			</div>
		</div>

		<AccountSettingsModal
			:isOpen="modalValiable"
			:user="userInfo"
			@close="modalValiable = false" />
	</header>
</template>

<script>
import AccountSettingsModal from "@/components/AccountSettingsModal.vue";
import { ref } from "vue";

export default {
	name: "HeaderBar",
	components: { AccountSettingsModal },

	data() {
		const modalValiable = ref(false);
		console.log("HeaderBar component loaded", modalValiable);
		return {
			isDropdownOpen: false,
			modalValiable: false,
		};
	},

	computed: {
		userInfo() {
			return this.$store.state.auth.user || {};
		},
		forAdmin() {
			const type = Number(this.userInfo.user_type);
			return type !== 1 && type !== 2;
		},
		forClient() {
			const type = Number(this.userInfo.user_type);
			return type !== 1 && type == 2;
		},
		initials() {
			const name = this.userInfo.organization_name || this.userInfo.name || "T";
			return name
				.split(" ")
				.map((w) => w[0])
				.join("")
				.slice(0, 2)
				.toUpperCase();
		},
	},

	methods: {
		toggleDropdown() {
			this.isDropdownOpen = !this.isDropdownOpen;
		},
		logout() {
			this.$store.dispatch("auth/logout").then(() => {
				this.$router.push("/register");
			});
		},
		accountSettings() {
			this.modalValiable = !this.modalValiable;
		},
		handleClickOutside(event) {
			if (!this.$el.contains(event.target)) {
				this.isDropdownOpen = false;
			}
		},
	},

	mounted() {
		document.addEventListener("click", this.handleClickOutside);
	},
	beforeUnmount() {
		document.removeEventListener("click", this.handleClickOutside);
	},
};
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
	transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
	opacity: 0;
	transform: translateY(-6px) scale(0.97);
}
</style>
